import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, ListOrdered, Upload, BarChart3, Sparkles, LogOut, MoonStar, CreditCard, Link2, Share2, CalendarDays, BookOpen, MessageCircle, PanelLeftClose, PanelLeftOpen, Video, Film, Folder, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import TrialBadge from "@/components/TrialBadge";
import LiveTicker from "@/components/LiveTicker";
import WhatsNew, { useWhatsNew } from "@/components/WhatsNew";
import { Wordmark, LogoIcon } from "@/components/Wordmark";
import { useTheme } from "@/theme/ThemeProvider";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Nav order per user request (Feb 18, 2026):
// "Dashboard, calendar, analytics, recap, lets put trades near the bottom as i wouldnt
// be there as much" — so Trades sits well below the daily-use pages.
const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/calendar", icon: CalendarDays, label: "Calendar", testid: "nav-calendar" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", testid: "nav-analytics" },
  { to: "/recap", icon: MoonStar, label: "Daily Recap", testid: "nav-recap" },
  { to: "/replay", icon: Video, label: "Replay", testid: "nav-replay", badge: "PRO" },
  { to: "/coach", icon: Sparkles, label: "Coach", testid: "nav-coach" },
  { to: "/journal", icon: BookOpen, label: "Journal", testid: "nav-journal" },
  { to: "/import", icon: Upload, label: "Import", testid: "nav-import" },
  { to: "/trades", icon: ListOrdered, label: "Trades", testid: "nav-trades" },
  { to: "/connections", icon: Link2, label: "Brokers", testid: "nav-connections" },
  { to: "/share", icon: Share2, label: "Share Card", testid: "nav-share" },
  { to: "/contact", icon: MessageCircle, label: "Contact Us", testid: "nav-contact" },
];

const SIDEBAR_COLLAPSE_KEY = "tg_sidebar_collapsed";

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const wn = useWhatsNew();
  const { theme } = useTheme();

  // Hide "PRO" nav badges once the user is actually on Pro — they know, no need
  // to keep advertising it. Non-Pro (starter/trial/expired) still sees them so
  // the upsell surfaces from the nav.
  const isPro = user?.plan_tier === "pro" || user?.subscription_status === "active_promo" || user?.subscription_status === "active_manual_grant";

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === "1";
  });

  // Billing status — used to reveal a "MANAGE MEMBERSHIP" link once the user has
  // ever paid (so past-due / canceled customers can still reach the Stripe portal
  // to update payment methods, view receipts, or cancel).
  const { data: billing } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
    staleTime: 60_000,
  });
  const hasPortalAccess = !!(billing?.has_stripe_customer || billing?.is_subscribed);
  const [portalLoading, setPortalLoading] = useState(false);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/billing/portal", { return_url: `${window.location.origin}/pricing` });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Billing portal unavailable — please contact support.");
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    try { window.localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  const sidebarWidth = collapsed ? 68 : 248;

  return (
    <div className="min-h-screen flex relative" style={{ background: "var(--bg)", color: "var(--fg)", fontFamily: "var(--font-body)" }}>
      {/* Aurora background — subtle green + violet radial glows anchored to the corners.
          This is what gives the app that "expensive" MorEdge-like depth without stealing
          their identity. Fixed position so it stays put during scroll, pointer-events-none
          so it never interferes with clicks. */}
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 900px 500px at 8% 5%, rgba(34,197,94,0.10), transparent 60%),
            radial-gradient(ellipse 900px 500px at 92% 105%, rgba(139,92,246,0.12), transparent 60%),
            radial-gradient(ellipse 1200px 700px at 50% 50%, rgba(139,92,246,0.03), transparent 70%)
          `,
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-[0.025]"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
      <WhatsNew open={wn.open} onClose={wn.closeDrawer} />
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className="shrink-0 sticky top-0 h-screen flex flex-col transition-[width] duration-200 ease-out"
        style={{
          width: `${sidebarWidth}px`,
          background: "rgba(10,10,11,0.6)",
          backdropFilter: "blur(20px)",
          borderRight: "1px solid var(--border)",
        }}
      >
        <div
          className={`${collapsed ? "px-2" : "px-5"} pt-6 pb-5 transition-colors relative`}
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div className="flex items-center justify-between gap-2">
            {!collapsed && (
              <div
                className="flex items-center gap-2 relative cursor-pointer flex-1"
                onClick={wn.openDrawer}
                data-testid="brand-logo"
                title="Trade Clipping — the killer feature"
              >
                <Wordmark theme={theme} size="md" variant={isPro ? "purple" : "green"} />
                {wn.hasUnread && (
                  <span className="relative flex h-2 w-2 ml-1" data-testid="whats-new-dot">
                    <span className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping" style={{ background: isPro ? "var(--accent)" : "var(--green)" }} />
                    <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: isPro ? "var(--accent)" : "var(--green)", boxShadow: isPro ? "var(--accent-glow)" : "var(--green-glow)" }} />
                  </span>
                )}
              </div>
            )}
            {collapsed && (
              <div
                onClick={wn.openDrawer}
                data-testid="brand-logo"
                className="mx-auto cursor-pointer flex items-center justify-center h-8 w-8"
                title="TradeGreen"
              >
                <LogoIcon size={28} variant={isPro ? "purple" : "green"} />
              </div>
            )}
            <button
              onClick={() => setCollapsed((c) => !c)}
              data-testid="sidebar-toggle"
              className="p-1.5 rounded-md hover:bg-white/5 transition-colors shrink-0"
              style={{ color: "var(--fg-3)" }}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? <PanelLeftOpen className="h-4 w-4" strokeWidth={1.5} /> : <PanelLeftClose className="h-4 w-4" strokeWidth={1.5} />}
            </button>
          </div>
          {!collapsed && <div className="mt-4"><TrialBadge /></div>}
        </div>

        <nav className={`flex-1 ${collapsed ? "px-2" : "px-3"} py-4 space-y-1 overflow-y-auto`}>
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center" : "gap-3"} px-3 py-2.5 text-sm transition-all ${
                  isActive ? "font-medium" : ""
                }`
              }
              style={({ isActive }) => ({
                background: isActive ? "var(--accent-soft)" : "transparent",
                color: isActive ? "var(--accent)" : "var(--fg-2)",
                borderRadius: "10px",
              })}
            >
              <it.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              {!collapsed && (
                <span className="flex items-center gap-2 flex-1">
                  {it.label}
                  {it.badge && !isPro && (
                    <span className="text-[9px] font-mono-num px-1.5 py-0.5 tracking-widest" style={{ background: "var(--accent)", color: "white", borderRadius: "4px" }}>
                      {it.badge}
                    </span>
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className={`${collapsed ? "px-2" : "px-3"} py-3`} style={{ borderTop: "1px solid var(--border)" }}>
          {!collapsed ? (
            <>
              <NavLink to="/pricing" data-testid="nav-pricing"
                className="flex items-center gap-3 px-3 py-2 text-xs mb-2 transition-colors"
                style={({ isActive }) => ({
                  background: isActive ? "var(--surface2)" : "transparent",
                  color: isActive ? "var(--green)" : "var(--fg-3)",
                })}>
                <CreditCard className="h-3.5 w-3.5" strokeWidth={1.5} /> SUBSCRIPTION
              </NavLink>
              {hasPortalAccess && (
                <button
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                  data-testid="nav-manage-membership"
                  className="w-full flex items-center gap-3 px-3 py-2 text-xs mb-2 transition-colors hover:text-[color:var(--accent)] disabled:opacity-50"
                  style={{ color: "var(--fg-3)" }}
                  title="Update payment method, view receipts, change plan, or cancel"
                >
                  <Settings className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {portalLoading ? "OPENING…" : "MANAGE MEMBERSHIP"}
                </button>
              )}
              <div className="px-2 mb-2">
                <div className="text-[9px] font-mono tracking-widest" style={{ color: "var(--fg-3)" }}>OPERATOR</div>
                <div className="text-sm truncate" style={{ color: "var(--fg)" }} data-testid="user-name">{user?.name || user?.email}</div>
              </div>
              <button
                onClick={logout}
                data-testid="logout-button"
                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:text-rose-400 transition-colors"
                style={{ color: "var(--fg-3)" }}
              >
                <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> SIGN OUT
              </button>
            </>
          ) : (
            <>
              <NavLink to="/pricing" data-testid="nav-pricing" title="Subscription"
                className="flex items-center justify-center py-2 mb-1 transition-colors rounded-md"
                style={({ isActive }) => ({
                  background: isActive ? "var(--surface2)" : "transparent",
                  color: isActive ? "var(--green)" : "var(--fg-3)",
                })}>
                <CreditCard className="h-4 w-4" strokeWidth={1.5} />
              </NavLink>
              {hasPortalAccess && (
                <button
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                  data-testid="nav-manage-membership"
                  title="Manage membership"
                  className="w-full flex items-center justify-center py-2 mb-1 transition-colors rounded-md hover:text-[color:var(--accent)] disabled:opacity-50"
                  style={{ color: "var(--fg-3)" }}
                >
                  <Settings className="h-4 w-4" strokeWidth={1.5} />
                </button>
              )}
              <button
                onClick={logout}
                data-testid="logout-button"
                title="Sign out"
                className="w-full flex items-center justify-center py-2 hover:text-rose-400 transition-colors rounded-md"
                style={{ color: "var(--fg-3)" }}
              >
                <LogOut className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <LiveTicker />
        {children}
      </main>
    </div>
  );
}
